'use client'
import { useCurrencyPrice } from '@repo/ui'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toSlug } from '@/utils/common'
import { getStandingDiscount } from '@/utils/discount'
import {
  getValidImages,
  PropertyImageSection,
  PropertyLocation,
  PropertyReviews,
  HomePriceSection,
  NonHomePriceSection,
  PropertyUnitFacts,
} from './PropertyCardParts'

const PropertyCard = ({
  className = '',
  property,
  queryPrams = '',
  isFullyVisible = true,
  enableTouch = false,
  propertylisting = false,
  view = '',
  city = '',
  ishome = false,
  similarproperty = false,
}) => {
  const DRAG_THRESHOLD = 8
  const { displayPrice, displayAlternatePrice } = useCurrencyPrice()
  const [isDesktop, setIsDesktop] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const dragStartRef = useRef(null)
  const router = useRouter()

  // All published rates already include the standing 10% discount. Surface the
  // pre-discount ("original") price so the saving is visible on every card.
  const discount = getStandingDiscount(property?.daily_rate)
  const isDiscounted = discount.hasDiscount
  const discountPct = discount.percent
  const originalPrice = discount.originalPrice

  const cityName = city || property?.city || ''
  const cityPass = toSlug(cityName)
  const propertyHref = `/${cityPass}/${property?.slug}${queryPrams}`

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    setIsDesktop(mq.matches)
    const handler = (e) => setIsDesktop(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const handleLinkClick = (e) => {
    // Prevent navigation when the click is the end of a drag gesture.
    // The `property_view` event now fires on the detail page itself
    // (PropertyViewTracker), not on this card click.
    if (isDragging) {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
    }
  }

  const getPointerCoordinates = (event) => {
    if ('touches' in event && event.touches.length > 0) {
      return {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      }
    }

    if ('changedTouches' in event && event.changedTouches.length > 0) {
      return {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
      }
    }

    return {
      x: event.clientX,
      y: event.clientY,
    }
  }

  const handleDragStart = (event) => {
    dragStartRef.current = getPointerCoordinates(event)
    setIsDragging(false)
  }

  const handleDragMove = (event) => {
    if (!dragStartRef.current) {
      return
    }

    const currentPosition = getPointerCoordinates(event)
    const deltaX = Math.abs(currentPosition.x - dragStartRef.current.x)
    const deltaY = Math.abs(currentPosition.y - dragStartRef.current.y)

    if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
      setIsDragging(true)
    }
  }

  const handleDragEnd = () => {
    dragStartRef.current = null

    if (isDragging) {
      window.setTimeout(() => setIsDragging(false), 0)
    }
  }

  const validImages = useMemo(() => {
    return getValidImages(property, similarproperty)
  }, [property, similarproperty])

  function handleCityClick(e) {
    e.preventDefault()
    e.stopPropagation()
    if (cityName) {
      router.push(`/property-listing?location=${encodeURIComponent(cityName)}`)
    }
  }

  return (
    <Link
      href={propertyHref}
      scroll
      prefetch
      className={`${className} block h-full`}
      onClick={handleLinkClick}
    >
      <div className='w-full h-full flex flex-col bg-white rounded-[20px] transition-shadow duration-200 hover:shadow-lg'>
        <PropertyImageSection
          validImages={validImages}
          property={property}
          similarproperty={similarproperty}
          isDesktop={isDesktop}
          enableTouch={enableTouch}
          isFullyVisible={isFullyVisible}
          ishome={ishome}
          view={view}
          displayPrice={displayPrice}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
        />

        <div
          className={`flex flex-1 flex-col ${ishome ? 'mt-3 px-3 pb-3 gap-2' : ''} ${view === 'grid' ? 'border border-[#EDEDED] rounded-b-[20px] p-4 bg-white' : ''}`}
        >
          <div className='flex flex-col gap-2 flex-grow'>
            <p
              className={ishome
                ? 'text-[15px] md:text-[16px] font-semibold text-[#191919] leading-[1.4] line-clamp-2 min-h-[44px]'
                : 'md:text-[16px] font-semibold text-[#1E1F24] md:min-w-[233px] lg:max-w-[27ch] leading-[1.5] line-clamp-2 h-[48px]'
              }
            >
              {property?.name || 'No Name'}
            </p>

            <div className='flex items-center justify-between gap-2'>
              <PropertyLocation cityName={cityName} onCityClick={handleCityClick} />
              <PropertyReviews
                ishome={ishome}
                property={property}
                similarproperty={similarproperty}
              />
            </div>

            <PropertyUnitFacts property={property} />

            {ishome && (
              <HomePriceSection
                displayPrice={displayPrice}
                property={property}
                isDiscounted={isDiscounted}
                discountPct={discountPct}
                originalPrice={originalPrice}
              />
            )}

            {!ishome && (
              <NonHomePriceSection
                property={property}
                displayPrice={displayPrice}
                displayAlternatePrice={displayAlternatePrice}
                isDiscounted={isDiscounted}
                discountPct={discountPct}
                originalPrice={originalPrice}
                showTooltip={showTooltip}
                setShowTooltip={setShowTooltip}
              />
            )}
          </div>
        </div>
      </div>
    </Link>
  )
}

export default PropertyCard
